import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { Category, ProductImage, ProductAttributes, CustomAttributeGroup, AttributePlacement } from '../../types';
import { adminAPI } from '../../services/api';
import { getImageUrl } from '../../utils/imageUrl';
import { normalizeOption, normalizeOptions } from '../../utils/attributeHelpers';
import RichTextEditor from '../common/RichTextEditor';
import './AdminFormStyles.css';

interface ProductFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<any>; // Returns created/updated product with ID
  product?: any | null;
  categories: Category[];
}

const ProductFormModal: React.FC<ProductFormModalProps> = ({
  isOpen,
  onClose,
  onSave,
  product,
  categories
}) => {
  const [formData, setFormData] = useState({
    name_bn: '',
    name_en: '',
    slug: '',
    category: '',
    summary_bn: '',
    summary_en: '',
    description_bn: '',
    description_en: '',
    price: '',
    discount_price: '',
    stock: '',
    image: null as File | null,
    is_active: true,
    is_featured: false,
    free_delivery: false
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<any>({});
  
  // Multi-image state
  const [existingImages, setExistingImages] = useState<ProductImage[]>([]);
  const [additionalImages, setAdditionalImages] = useState<File[]>([]);
  const [imageUploading, setImageUploading] = useState(false);
  
  // Attributes state
  interface OptionInputState {
    name: string;
    extra_price: string;
  }

  const defaultAttributes: ProductAttributes = {
    colors:  { enabled: false, placement: 'default', options: [] },
    sizes:   { enabled: false, placement: 'default', options: [] },
    weights: { enabled: false, placement: 'default', options: [] },
    custom:  [],
  };
  const [attributes, setAttributes] = useState<ProductAttributes>(defaultAttributes);
  const [attrInputs, setAttrInputs] = useState<{
    colors: OptionInputState;
    sizes: OptionInputState;
    weights: OptionInputState;
  }>({
    colors:  { name: '', extra_price: '' },
    sizes:   { name: '', extra_price: '' },
    weights: { name: '', extra_price: '' },
  });
  const [customAttrInputs, setCustomAttrInputs] = useState<Record<string, OptionInputState>>({});
  const [newGroupName, setNewGroupName] = useState('');
  const [showNewGroupInput, setShowNewGroupInput] = useState(false);

  // Add key for forcing RichTextEditor re-render
  const [editorKey, setEditorKey] = useState(0);

  useEffect(() => {
    if (product) {
      console.log('Editing product:', product);
      console.log('Description BN length:', product.description_bn?.length || 0);
      console.log('Description EN length:', product.description_en?.length || 0);
      console.log('Product images:', product.images);
      console.log('Number of images:', product.images?.length || 0);
      
      const newFormData = {
        name_bn: product.name_bn || '',
        name_en: product.name_en || '',
        slug: product.slug || '',
        category: product.category?.id || product.category || '',
        summary_bn: product.summary_bn || '',
        summary_en: product.summary_en || '',
        description_bn: product.description_bn || '',
        description_en: product.description_en || '',
        price: product.price || '',
        discount_price: product.discount_price || '',
        stock: product.stock || '',
        image: null,
        is_active: product.is_active ?? true,
        is_featured: product.is_featured ?? false,
        free_delivery: product.free_delivery ?? false
      };
      
      console.log('Setting formData with descriptions:', {
        bn: newFormData.description_bn.substring(0, 100),
        en: newFormData.description_en.substring(0, 100)
      });
      
      setFormData(newFormData);
      // Load existing images
      const images = product.images || [];
      console.log('Setting existingImages:', images);
      setExistingImages(images);
      // Load existing attributes
      const rawAttrs = product.attributes || {};
      setAttributes({
        colors:  rawAttrs.colors  ? { enabled: !!rawAttrs.colors.enabled, placement: rawAttrs.colors.placement || 'default', options: normalizeOptions(rawAttrs.colors.options) } : { enabled: false, placement: 'default', options: [] },
        sizes:   rawAttrs.sizes   ? { enabled: !!rawAttrs.sizes.enabled, placement: rawAttrs.sizes.placement || 'default', options: normalizeOptions(rawAttrs.sizes.options) } : { enabled: false, placement: 'default', options: [] },
        weights: rawAttrs.weights ? { enabled: !!rawAttrs.weights.enabled, placement: rawAttrs.weights.placement || 'default', options: normalizeOptions(rawAttrs.weights.options) } : { enabled: false, placement: 'default', options: [] },
        custom:  Array.isArray(rawAttrs.custom) ? rawAttrs.custom.map((cg: any) => ({
          id: cg.id || cg.name,
          name: cg.name || '',
          name_bn: cg.name_bn || cg.name || '',
          name_en: cg.name_en || cg.name || '',
          enabled: cg.enabled ?? true,
          placement: cg.placement || 'default',
          options: normalizeOptions(cg.options),
        })) : [],
      });
    } else {
      setFormData({
        name_bn: '',
        name_en: '',
        slug: '',
        category: '',
        summary_bn: '',
        summary_en: '',
        description_bn: '',
        description_en: '',
        price: '',
        discount_price: '',
        stock: '',
        image: null,
        is_active: true,
        is_featured: false,
        free_delivery: false
      });
      setExistingImages([]);
      setAttributes({
        colors: { enabled: false, options: [] },
        sizes: { enabled: false, options: [] },
        weights: { enabled: false, options: [] },
        custom: []
      });
    }
    setAdditionalImages([]);
    setAttrInputs({
      colors: { name: '', extra_price: '' },
      sizes: { name: '', extra_price: '' },
      weights: { name: '', extra_price: '' }
    });
    setCustomAttrInputs({});
    setNewGroupName('');
    setShowNewGroupInput(false);
    setErrors({});
    // Increment key synchronously to force RichTextEditor remount
    setEditorKey(prev => prev + 1);
  }, [product, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;

    if (type === 'file') {
      const files = (e.target as HTMLInputElement).files;
      if (files && files.length > 0) {
        setFormData(prev => ({
          ...prev,
          image: files[0]
        }));
      }
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors((prev: any) => ({ ...prev, [name]: null }));
    }
  };

  const handleRichTextChange = (name: string, value: string) => {
    // Guard: don't let Quill's initialization onChange overwrite real content with empty
    const isQuillEmpty = (html: string) => {
      if (!html) return true;
      return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim() === '';
    };

    setFormData(prev => {
      const current = prev[name as keyof typeof prev];
      // If Quill fires onChange with empty content but formData already has real content, skip
      if (typeof current === 'string' && current.length > 15 && isQuillEmpty(value)) {
        return prev;
      }
      return { ...prev, [name]: value };
    });

    if (errors[name]) {
      setErrors((prev: any) => ({ ...prev, [name]: null }));
    }
  };

  // Helper function to check if HTML content is empty
  const isHtmlEmpty = (html: string): boolean => {
    if (!html || html.trim() === '') return true;
    
    // Remove HTML tags and check if there's actual content
    const text = html.replace(/<[^>]*>/g, '').trim();
    return text === '';
  };

  const handleAdditionalImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      setAdditionalImages(prev => [...prev, ...Array.from(files)]);
    }
  };

  const removeAdditionalImage = (index: number) => {
    setAdditionalImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteExistingImage = async (imageId: number) => {
    if (!product?.id) return;
    
    try {
      await adminAPI.deleteProductImage(product.id, imageId);
      setExistingImages(prev => prev.filter(img => img.id !== imageId));
    } catch (error) {
      console.error('Failed to delete image:', error);
    }
  };

  const generateSlug = () => {
    const slug = formData.name_en
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    setFormData(prev => ({ ...prev, slug }));
  };

  // ── Attribute helpers ─────────────────────────────────────────────
  type AttrKey = 'colors' | 'sizes' | 'weights';

  const toggleAttr = (key: AttrKey) => {
    setAttributes(prev => ({
      ...prev,
      [key]: { ...prev[key]!, enabled: !prev[key]!.enabled },
    }));
  };

  const addAttrOption = (key: AttrKey) => {
    const input = attrInputs[key];
    const val = input.name.trim();
    if (!val) return;
    const extraPrice = parseFloat(input.extra_price) || 0;

    setAttributes(prev => {
      const existing = normalizeOptions(prev[key]?.options);
      if (existing.some(o => o.name.toLowerCase() === val.toLowerCase())) return prev;
      return {
        ...prev,
        [key]: {
          ...prev[key]!,
          options: [...existing, { name: val, extra_price: extraPrice }]
        }
      };
    });
    setAttrInputs(prev => ({ ...prev, [key]: { name: '', extra_price: '' } }));
  };

  const removeAttrOption = (key: AttrKey, optionName: string) => {
    setAttributes(prev => ({
      ...prev,
      [key]: {
        ...prev[key]!,
        options: normalizeOptions(prev[key]?.options).filter(o => o.name !== optionName)
      }
    }));
  };

  const handleAttrKeyDown = (key: AttrKey, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); addAttrOption(key); }
  };

  // Custom Attribute Groups helpers
  const addCustomGroup = () => {
    const name = newGroupName.trim();
    if (!name) return;
    const id = 'custom_' + Date.now();
    setAttributes(prev => ({
      ...prev,
      custom: [
        ...(prev.custom || []),
        { id, name, name_bn: name, name_en: name, enabled: true, placement: 'default', options: [] }
      ]
    }));
    setNewGroupName('');
    setShowNewGroupInput(false);
  };

  const removeCustomGroup = (groupId: string) => {
    setAttributes(prev => ({
      ...prev,
      custom: (prev.custom || []).filter(g => g.id !== groupId)
    }));
  };

  const toggleCustomGroup = (groupId: string) => {
    setAttributes(prev => ({
      ...prev,
      custom: (prev.custom || []).map(g => g.id === groupId ? { ...g, enabled: !g.enabled } : g)
    }));
  };

  const addCustomOption = (groupId: string) => {
    const input = customAttrInputs[groupId] || { name: '', extra_price: '' };
    const val = input.name.trim();
    if (!val) return;
    const extraPrice = parseFloat(input.extra_price) || 0;

    setAttributes(prev => ({
      ...prev,
      custom: (prev.custom || []).map(g => {
        if (g.id !== groupId) return g;
        const existing = normalizeOptions(g.options);
        if (existing.some(o => o.name.toLowerCase() === val.toLowerCase())) return g;
        return {
          ...g,
          options: [...existing, { name: val, extra_price: extraPrice }]
        };
      })
    }));

    setCustomAttrInputs(prev => ({ ...prev, [groupId]: { name: '', extra_price: '' } }));
  };

  const removeCustomOption = (groupId: string, optionName: string) => {
    setAttributes(prev => ({
      ...prev,
      custom: (prev.custom || []).map(g => {
        if (g.id !== groupId) return g;
        return {
          ...g,
          options: normalizeOptions(g.options).filter(o => o.name !== optionName)
        };
      })
    }));
  };

  const updateCustomGroupPlacement = (groupId: string, placement: AttributePlacement) => {
    setAttributes(prev => ({
      ...prev,
      custom: (prev.custom || []).map(g => (g.id === groupId || g.name === groupId) ? { ...g, placement } : g)
    }));
  };

  const updateAttrPlacement = (key: AttrKey, placement: AttributePlacement) => {
    setAttributes(prev => ({
      ...prev,
      [key]: { ...prev[key]!, placement }
    }));
  };
  // ─────────────────────────────────────────────────────────────────

  const validate = () => {
    const newErrors: any = {};

    if (!formData.name_bn.trim()) newErrors.name_bn = 'Bangla name is required';
    if (!formData.name_en.trim()) newErrors.name_en = 'English name is required';
    if (!formData.slug.trim()) newErrors.slug = 'Slug is required';
    if (!formData.category) newErrors.category = 'Category is required';
    
    // Validate rich text content
    if (isHtmlEmpty(formData.description_bn)) {
      newErrors.description_bn = 'Bangla description is required';
    }
    if (isHtmlEmpty(formData.description_en)) {
      newErrors.description_en = 'English description is required';
    }
    
    if (!formData.price || parseFloat(formData.price) <= 0) newErrors.price = 'Price must be greater than 0';
    if (formData.discount_price && parseFloat(formData.discount_price) >= parseFloat(formData.price)) {
      newErrors.discount_price = 'Discount price must be less than regular price';
    }
    if (!formData.stock || parseInt(formData.stock) < 0) newErrors.stock = 'Stock must be 0 or greater';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);
    try {
      const data = new FormData();
      data.append('name_bn', formData.name_bn);
      data.append('name_en', formData.name_en);
      data.append('slug', formData.slug);
      data.append('category', formData.category);
      
      // Clean up HTML content - if empty, send empty string
      const cleanSummaryBn = isHtmlEmpty(formData.summary_bn) ? '' : formData.summary_bn;
      const cleanSummaryEn = isHtmlEmpty(formData.summary_en) ? '' : formData.summary_en;
      const cleanDescriptionBn = isHtmlEmpty(formData.description_bn) ? '' : formData.description_bn;
      const cleanDescriptionEn = isHtmlEmpty(formData.description_en) ? '' : formData.description_en;
      
      data.append('summary_bn', cleanSummaryBn);
      data.append('summary_en', cleanSummaryEn);
      data.append('description_bn', cleanDescriptionBn);
      data.append('description_en', cleanDescriptionEn);
      data.append('price', formData.price);
      if (formData.discount_price) data.append('discount_price', formData.discount_price);
      data.append('stock', formData.stock);
      data.append('is_active', formData.is_active.toString());
      data.append('is_featured', formData.is_featured.toString());
      data.append('free_delivery', formData.free_delivery.toString());

      // Append attributes as JSON string (parsed by backend)
      data.append('attributes', JSON.stringify(attributes));

      if (formData.image instanceof File) {
        data.append('image', formData.image);
      }

      // onSave returns the created/updated product
      const savedProduct = await onSave(data);
      
      // Upload additional images if any
      // Use product.id for existing products, or savedProduct.id for newly created
      const productId = product?.id || savedProduct?.id;
      if (additionalImages.length > 0 && productId) {
        setImageUploading(true);
        try {
          await adminAPI.uploadProductImages(productId, additionalImages);
        } catch (imgError) {
          console.error('Failed to upload additional images:', imgError);
        }
        setImageUploading(false);
      }
      
      onClose();
    } catch (error: any) {
      console.error('Form submission error:', error);
      if (error.response?.data) {
        setErrors(error.response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={product ? 'Edit Product' : 'Add Product'}
      size="large"
    >
      <form onSubmit={handleSubmit} className="admin-form-modal">
        <div className="admin-form-table">
          {/* Name Bangla */}
          <div className="admin-form-row">
            <div className="admin-form-label">Name (Bangla) *</div>
            <div className="admin-form-input">
              <input
                type="text"
                name="name_bn"
                value={formData.name_bn}
                onChange={handleChange}
                placeholder="পণ্যের নাম লিখুন"
                className={errors.name_bn ? 'has-error' : ''}
              />
              {errors.name_bn && <span className="error-text">{errors.name_bn}</span>}
            </div>
          </div>

          {/* Name English */}
          <div className="admin-form-row">
            <div className="admin-form-label">Name (English) *</div>
            <div className="admin-form-input">
              <input
                type="text"
                name="name_en"
                value={formData.name_en}
                onChange={handleChange}
                onBlur={generateSlug}
                placeholder="Enter product name"
                className={errors.name_en ? 'has-error' : ''}
              />
              {errors.name_en && <span className="error-text">{errors.name_en}</span>}
            </div>
          </div>

          {/* Slug */}
          <div className="admin-form-row">
            <div className="admin-form-label">Slug *</div>
            <div className="admin-form-input">
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                placeholder="product-url-slug"
                className={errors.slug ? 'has-error' : ''}
              />
              {errors.slug && <span className="error-text">{errors.slug}</span>}
              <span className="admin-form-hint">Auto-generated from English name</span>
            </div>
          </div>

          {/* Category */}
          <div className="admin-form-row">
            <div className="admin-form-label">Category *</div>
            <div className="admin-form-input">
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className={errors.category ? 'has-error' : ''}
              >
                <option value="">Select Category</option>
                {Array.isArray(categories) && categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name_en}
                  </option>
                ))}
              </select>
              {errors.category && <span className="error-text">{errors.category}</span>}
            </div>
          </div>

          {/* Main Product Image */}
          <div className="admin-form-row">
            <div className="admin-form-label">Main Image</div>
            <div className="admin-form-input">
              <input
                type="file"
                name="image"
                onChange={handleChange}
                accept="image/*"
              />
              {formData.image && (
                <span className="admin-form-hint">Selected: {formData.image.name}</span>
              )}
              {product?.image && !formData.image && (
                <div style={{ marginTop: '8px' }}>
                  <img 
                    src={getImageUrl(product.image)} 
                    alt="Current" 
                    style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                  />
                  <span className="admin-form-hint" style={{ marginLeft: '8px' }}>Current main image</span>
                </div>
              )}
            </div>
          </div>

          {/* Additional Images Section - Always show for gallery uploads */}
          <div className="admin-form-row">
            <div className="admin-form-label">Gallery Images</div>
            <div className="admin-form-input">
              {/* Existing Images Grid - Only when editing */}
              {product?.id && existingImages.length > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '10px', 
                  marginBottom: '12px',
                  padding: '12px',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  {existingImages.map((img) => (
                    <div key={img.id} style={{ position: 'relative' }}>
                      <img 
                        src={getImageUrl(img.image)} 
                        alt="Product" 
                        style={{ 
                          width: '70px', 
                          height: '70px', 
                          objectFit: 'cover', 
                          borderRadius: '6px',
                          border: img.is_primary ? '2px solid #10b981' : '1px solid #e5e7eb'
                        }}
                      />
                      {img.is_primary && (
                        <span style={{
                          position: 'absolute',
                          top: '-6px',
                          left: '-6px',
                          background: '#10b981',
                          color: 'white',
                          fontSize: '10px',
                          padding: '2px 4px',
                          borderRadius: '4px'
                        }}>
                          ★
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDeleteExistingImage(img.id)}
                        style={{
                          position: 'absolute',
                          top: '-6px',
                          right: '-6px',
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add More Images */}
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleAdditionalImagesChange}
                style={{ marginBottom: '8px' }}
              />
              <span className="admin-form-hint">Select multiple images to add to gallery</span>

              {/* Preview of new images to upload */}
              {additionalImages.length > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '8px', 
                  marginTop: '12px',
                  padding: '12px',
                  background: '#fef3c7',
                  borderRadius: '8px',
                  border: '1px dashed #d97706'
                }}>
                  <span style={{ fontSize: '12px', color: '#92400e', width: '100%', marginBottom: '4px' }}>
                    New images to upload ({additionalImages.length}):
                  </span>
                  {additionalImages.map((file, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                      <img 
                        src={URL.createObjectURL(file)} 
                        alt={`New ${index + 1}`}
                        style={{ 
                          width: '60px', 
                          height: '60px', 
                          objectFit: 'cover', 
                          borderRadius: '6px',
                          border: '1px solid #d97706'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => removeAdditionalImage(index)}
                        style={{
                          position: 'absolute',
                          top: '-5px',
                          right: '-5px',
                          background: '#6b7280',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '18px',
                          height: '18px',
                          fontSize: '10px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Summary Bangla */}
          <div className="admin-form-row">
            <div className="admin-form-label">Summary (BN)</div>
            <div className="admin-form-input">
              <RichTextEditor
                key={`summary-bn-${product?.id || 'new'}-${editorKey}`}
                value={product?.summary_bn || ''}
                onChange={(value) => handleRichTextChange('summary_bn', value)}
                placeholder="সংক্ষিপ্ত বিবরণ..."
                minHeight="150px"
              />
              <span className="admin-form-hint">Product summary for quick overview</span>
            </div>
          </div>

          {/* Summary English */}
          <div className="admin-form-row">
            <div className="admin-form-label">Summary (EN)</div>
            <div className="admin-form-input">
              <RichTextEditor
                key={`summary-en-${product?.id || 'new'}-${editorKey}`}
                value={product?.summary_en || ''}
                onChange={(value) => handleRichTextChange('summary_en', value)}
                placeholder="Brief summary..."
                minHeight="150px"
              />
              <span className="admin-form-hint">Product summary for quick overview</span>
            </div>
          </div>

          {/* Description Bangla */}
          <div className="admin-form-row">
            <div className="admin-form-label">Description (BN) *</div>
            <div className="admin-form-input">
              <RichTextEditor
                key={`description-bn-${product?.id || 'new'}-${editorKey}`}
                value={product?.description_bn || ''}
                onChange={(value) => handleRichTextChange('description_bn', value)}
                placeholder="পণ্যের বিস্তারিত বর্ণনা..."
                className={errors.description_bn ? 'has-error' : ''}
                minHeight="250px"
              />
              {errors.description_bn && <span className="error-text">{errors.description_bn}</span>}
            </div>
          </div>

          {/* Description English */}
          <div className="admin-form-row">
            <div className="admin-form-label">Description (EN) *</div>
            <div className="admin-form-input">
              <RichTextEditor
                key={`description-en-${product?.id || 'new'}-${editorKey}`}
                value={product?.description_en || ''}
                onChange={(value) => handleRichTextChange('description_en', value)}
                placeholder="Enter product description..."
                className={errors.description_en ? 'has-error' : ''}
                minHeight="250px"
              />
              {errors.description_en && <span className="error-text">{errors.description_en}</span>}
            </div>
          </div>

          {/* Price */}
          <div className="admin-form-row">
            <div className="admin-form-label">Price (৳) *</div>
            <div className="admin-form-input">
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                step="0.01"
                min="0"
                placeholder="0.00"
                className={errors.price ? 'has-error' : ''}
              />
              {errors.price && <span className="error-text">{errors.price}</span>}
            </div>
          </div>

          {/* Discount Price */}
          <div className="admin-form-row">
            <div className="admin-form-label">Discount Price (৳)</div>
            <div className="admin-form-input">
              <input
                type="number"
                name="discount_price"
                value={formData.discount_price}
                onChange={handleChange}
                step="0.01"
                min="0"
                placeholder="0.00"
                className={errors.discount_price ? 'has-error' : ''}
              />
              {errors.discount_price && <span className="error-text">{errors.discount_price}</span>}
            </div>
          </div>

          {/* Stock */}
          <div className="admin-form-row">
            <div className="admin-form-label">Stock *</div>
            <div className="admin-form-input">
              <input
                type="number"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
                min="0"
                placeholder="0"
                className={errors.stock ? 'has-error' : ''}
              />
              {errors.stock && <span className="error-text">{errors.stock}</span>}
            </div>
          </div>

          {/* ── Product Attributes ───────────────────────────────────────── */}
          <div className="admin-form-row">
            <div className="admin-form-label" style={{ paddingTop: '8px' }}>Attributes</div>
            <div className="admin-form-input">
              <div className="attr-section">
                <p className="attr-section-hint">
                  প্রোডাক্টের অপশন/ভ্যারিয়েন্ট তৈরি করুন। প্রতি অপশনে প্রয়োজনে অতিরিক্ত মূল্য (+৳) দিতে পারেন (যেমন: হিজাব ছাড়া ০৳, হিজাব সহ ৩০০৳)।
                </p>

                {/* Color */}
                <div className="attr-group">
                  <label className="attr-toggle">
                    <input
                      type="checkbox"
                      checked={attributes.colors?.enabled ?? false}
                      onChange={() => toggleAttr('colors')}
                    />
                    <span className="attr-toggle-label">
                      <span className="attr-swatch" style={{ background: 'linear-gradient(135deg,#f87171,#60a5fa,#34d399)' }}></span>
                      Color (কালার)
                    </span>
                  </label>
                  {attributes.colors?.enabled && (
                    <div className="attr-options">
                      <div className="attr-placement-selector">
                        <span className="attr-placement-label">📍 পজিশন:</span>
                        <div className="attr-placement-pills">
                          <button
                            type="button"
                            className={`attr-placement-pill ${(!attributes.colors.placement || attributes.colors.placement === 'default') ? 'active' : ''}`}
                            onClick={() => updateAttrPlacement('colors', 'default')}
                            title="প্রোডাক্ট সামারির নিচে ও কার্ট বাটনের উপরে"
                          >
                            📦 ডিফল্ট
                          </button>
                          <button
                            type="button"
                            className={`attr-placement-pill below-price ${attributes.colors.placement === 'below_price' ? 'active' : ''}`}
                            onClick={() => updateAttrPlacement('colors', 'below_price')}
                            title="প্রাইসের ঠিক নিচে ডিসপ্লে হবে"
                          >
                            💰 প্রাইসের নিচে
                          </button>
                        </div>
                      </div>
                      <div className="attr-chips">
                        {normalizeOptions(attributes.colors.options).map(opt => (
                          <span key={opt.name} className="attr-chip color-chip">
                            {opt.name}
                            {opt.extra_price > 0 && (
                              <span className="attr-price-badge">+৳{opt.extra_price}</span>
                            )}
                            <button type="button" onClick={() => removeAttrOption('colors', opt.name)}>×</button>
                          </span>
                        ))}
                      </div>
                      <div className="attr-add-row">
                        <input
                          type="text"
                          className="attr-name-input"
                          value={attrInputs.colors.name}
                          onChange={e => setAttrInputs(prev => ({ ...prev, colors: { ...prev.colors, name: e.target.value } }))}
                          onKeyDown={e => handleAttrKeyDown('colors', e)}
                          placeholder="কালারের নাম (যেমন: পেঁয়াজ কালার, কালো)"
                        />
                        <input
                          type="number"
                          className="attr-price-input"
                          value={attrInputs.colors.extra_price}
                          onChange={e => setAttrInputs(prev => ({ ...prev, colors: { ...prev.colors, extra_price: e.target.value } }))}
                          onKeyDown={e => handleAttrKeyDown('colors', e)}
                          placeholder="+৳ Extra (0)"
                          min="0"
                        />
                        {parseFloat(attrInputs.colors.extra_price) > 0 && parseFloat(formData.discount_price || formData.price) > 0 && (
                          <span className="attr-price-preview">
                            মোট: ৳{(parseFloat(formData.discount_price || formData.price) + parseFloat(attrInputs.colors.extra_price))}
                          </span>
                        )}
                        <button type="button" className="attr-add-btn" onClick={() => addAttrOption('colors')}>+ Add</button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Size */}
                <div className="attr-group">
                  <label className="attr-toggle">
                    <input
                      type="checkbox"
                      checked={attributes.sizes?.enabled ?? false}
                      onChange={() => toggleAttr('sizes')}
                    />
                    <span className="attr-toggle-label">
                      <span className="attr-icon">📐</span>
                      Size (সাইজ)
                    </span>
                  </label>
                  {attributes.sizes?.enabled && (
                    <div className="attr-options">
                      <div className="attr-placement-selector">
                        <span className="attr-placement-label">📍 পজিশন:</span>
                        <div className="attr-placement-pills">
                          <button
                            type="button"
                            className={`attr-placement-pill ${(!attributes.sizes.placement || attributes.sizes.placement === 'default') ? 'active' : ''}`}
                            onClick={() => updateAttrPlacement('sizes', 'default')}
                            title="প্রোডাক্ট সামারির নিচে ও কার্ট বাটনের উপরে"
                          >
                            📦 ডিফল্ট
                          </button>
                          <button
                            type="button"
                            className={`attr-placement-pill below-price ${attributes.sizes.placement === 'below_price' ? 'active' : ''}`}
                            onClick={() => updateAttrPlacement('sizes', 'below_price')}
                            title="প্রাইসের ঠিক নিচে ডিসপ্লে হবে"
                          >
                            💰 প্রাইসের নিচে
                          </button>
                        </div>
                      </div>
                      <div className="attr-chips">
                        {normalizeOptions(attributes.sizes.options).map(opt => (
                          <span key={opt.name} className="attr-chip size-chip">
                            {opt.name}
                            {opt.extra_price > 0 && (
                              <span className="attr-price-badge">+৳{opt.extra_price}</span>
                            )}
                            <button type="button" onClick={() => removeAttrOption('sizes', opt.name)}>×</button>
                          </span>
                        ))}
                      </div>
                      <div className="attr-add-row">
                        <input
                          type="text"
                          className="attr-name-input"
                          value={attrInputs.sizes.name}
                          onChange={e => setAttrInputs(prev => ({ ...prev, sizes: { ...prev.sizes, name: e.target.value } }))}
                          onKeyDown={e => handleAttrKeyDown('sizes', e)}
                          placeholder="সাইজ (যেমন: 50, 52, 54, M, L, XL)"
                        />
                        <input
                          type="number"
                          className="attr-price-input"
                          value={attrInputs.sizes.extra_price}
                          onChange={e => setAttrInputs(prev => ({ ...prev, sizes: { ...prev.sizes, extra_price: e.target.value } }))}
                          onKeyDown={e => handleAttrKeyDown('sizes', e)}
                          placeholder="+৳ Extra (0)"
                          min="0"
                        />
                        {parseFloat(attrInputs.sizes.extra_price) > 0 && parseFloat(formData.discount_price || formData.price) > 0 && (
                          <span className="attr-price-preview">
                            মোট: ৳{(parseFloat(formData.discount_price || formData.price) + parseFloat(attrInputs.sizes.extra_price))}
                          </span>
                        )}
                        <button type="button" className="attr-add-btn" onClick={() => addAttrOption('sizes')}>+ Add</button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Weight */}
                <div className="attr-group">
                  <label className="attr-toggle">
                    <input
                      type="checkbox"
                      checked={attributes.weights?.enabled ?? false}
                      onChange={() => toggleAttr('weights')}
                    />
                    <span className="attr-toggle-label">
                      <span className="attr-icon">⚖️</span>
                      Weight (ওজন / পরিমাণ)
                    </span>
                  </label>
                  {attributes.weights?.enabled && (
                    <div className="attr-options">
                      <div className="attr-placement-selector">
                        <span className="attr-placement-label">📍 পজিশন:</span>
                        <div className="attr-placement-pills">
                          <button
                            type="button"
                            className={`attr-placement-pill ${(!attributes.weights.placement || attributes.weights.placement === 'default') ? 'active' : ''}`}
                            onClick={() => updateAttrPlacement('weights', 'default')}
                            title="প্রোডাক্ট সামারির নিচে ও কার্ট বাটনের উপরে"
                          >
                            📦 ডিফল্ট
                          </button>
                          <button
                            type="button"
                            className={`attr-placement-pill below-price ${attributes.weights.placement === 'below_price' ? 'active' : ''}`}
                            onClick={() => updateAttrPlacement('weights', 'below_price')}
                            title="প্রাইসের ঠিক নিচে ডিসপ্লে হবে"
                          >
                            💰 প্রাইসের নিচে
                          </button>
                        </div>
                      </div>
                      <div className="attr-chips">
                        {normalizeOptions(attributes.weights.options).map(opt => (
                          <span key={opt.name} className="attr-chip weight-chip">
                            {opt.name}
                            {opt.extra_price > 0 && (
                              <span className="attr-price-badge">+৳{opt.extra_price}</span>
                            )}
                            <button type="button" onClick={() => removeAttrOption('weights', opt.name)}>×</button>
                          </span>
                        ))}
                      </div>
                      <div className="attr-add-row">
                        <input
                          type="text"
                          className="attr-name-input"
                          value={attrInputs.weights.name}
                          onChange={e => setAttrInputs(prev => ({ ...prev, weights: { ...prev.weights, name: e.target.value } }))}
                          onKeyDown={e => handleAttrKeyDown('weights', e)}
                          placeholder="পরিমাণ (যেমন: 500gm, 1kg)"
                        />
                        <input
                          type="number"
                          className="attr-price-input"
                          value={attrInputs.weights.extra_price}
                          onChange={e => setAttrInputs(prev => ({ ...prev, weights: { ...prev.weights, extra_price: e.target.value } }))}
                          onKeyDown={e => handleAttrKeyDown('weights', e)}
                          placeholder="+৳ Extra (0)"
                          min="0"
                        />
                        {parseFloat(attrInputs.weights.extra_price) > 0 && parseFloat(formData.discount_price || formData.price) > 0 && (
                          <span className="attr-price-preview">
                            মোট: ৳{(parseFloat(formData.discount_price || formData.price) + parseFloat(attrInputs.weights.extra_price))}
                          </span>
                        )}
                        <button type="button" className="attr-add-btn" onClick={() => addAttrOption('weights')}>+ Add</button>
                      </div>
                    </div>
                  )}
                </div>

                {/* ── Custom Attribute Groups (e.g. Hijab, Material, Packaging) ── */}
                {(attributes.custom || []).map(customGroup => {
                  const cInputs = customAttrInputs[customGroup.id] || { name: '', extra_price: '' };
                  const extraVal = parseFloat(cInputs.extra_price) || 0;
                  const currentBase = parseFloat(formData.discount_price || formData.price) || 0;
                  const cOptions = normalizeOptions(customGroup.options);

                  return (
                    <div className="attr-group" key={customGroup.id}>
                      <div className="custom-attr-header">
                        <label className="attr-toggle">
                          <input
                            type="checkbox"
                            checked={customGroup.enabled}
                            onChange={() => toggleCustomGroup(customGroup.id)}
                          />
                          <span className="attr-toggle-label">
                            <span className="attr-icon">✨</span>
                            {customGroup.name}
                          </span>
                        </label>
                        <button
                          type="button"
                          className="custom-attr-del-btn"
                          onClick={() => removeCustomGroup(customGroup.id)}
                          title="Delete this attribute group"
                        >
                          🗑️ গ্রুপ মুছুন
                        </button>
                      </div>

                      {customGroup.enabled && (
                        <div className="attr-options">
                          <div className="attr-placement-selector">
                            <span className="attr-placement-label">📍 ডিসপ্লে পজিশন:</span>
                            <div className="attr-placement-pills">
                              <button
                                type="button"
                                className={`attr-placement-pill ${(!customGroup.placement || customGroup.placement === 'default') ? 'active' : ''}`}
                                onClick={() => updateCustomGroupPlacement(customGroup.id, 'default')}
                                title="প্রোডাক্ট সামারির নিচে ও কার্ট বাটনের উপরে"
                              >
                                📦 ডিফল্ট পজিশন
                              </button>
                              <button
                                type="button"
                                className={`attr-placement-pill below-price ${customGroup.placement === 'below_price' ? 'active' : ''}`}
                                onClick={() => updateCustomGroupPlacement(customGroup.id, 'below_price')}
                                title="প্রোডাক্ট ডিটেইলস পেইজে প্রাইসের ঠিক নিচে ডিসপ্লে হবে"
                              >
                                💰 প্রাইসের নিচে (Below Price)
                              </button>
                            </div>
                          </div>
                          <div className="attr-chips">
                            {cOptions.map(opt => (
                              <span key={opt.name} className="attr-chip custom-chip">
                                {opt.name}
                                {opt.extra_price > 0 && (
                                  <span className="attr-price-badge">+৳{opt.extra_price}</span>
                                )}
                                <button
                                  type="button"
                                  onClick={() => removeCustomOption(customGroup.id, opt.name)}
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                          <div className="attr-add-row">
                            <input
                              type="text"
                              className="attr-name-input"
                              value={cInputs.name}
                              onChange={e => {
                                const val = e.target.value;
                                setCustomAttrInputs(prev => ({
                                  ...prev,
                                  [customGroup.id]: { ...(prev[customGroup.id] || { name: '', extra_price: '' }), name: val }
                                }));
                              }}
                              onKeyDown={e => {
                                if (e.key === 'Enter') { e.preventDefault(); addCustomOption(customGroup.id); }
                              }}
                              placeholder={`অপশনের নাম (যেমন: ${customGroup.name.includes('হিজাব') ? 'হিজাব সহ / হিজাব ছাড়া' : customGroup.name + ' অপশন'})`}
                            />
                            <input
                              type="number"
                              className="attr-price-input"
                              value={cInputs.extra_price}
                              onChange={e => {
                                const val = e.target.value;
                                setCustomAttrInputs(prev => ({
                                  ...prev,
                                  [customGroup.id]: { ...(prev[customGroup.id] || { name: '', extra_price: '' }), extra_price: val }
                                }));
                              }}
                              onKeyDown={e => {
                                if (e.key === 'Enter') { e.preventDefault(); addCustomOption(customGroup.id); }
                              }}
                              placeholder="+৳ Extra (0)"
                              min="0"
                            />
                            {extraVal > 0 && currentBase > 0 && (
                              <span className="attr-price-preview">মোট: ৳{currentBase + extraVal}</span>
                            )}
                            <button
                              type="button"
                              className="attr-add-btn"
                              onClick={() => addCustomOption(customGroup.id)}
                            >
                              + Add
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Add Custom Attribute Group Button */}
                {showNewGroupInput ? (
                  <div className="new-group-inline">
                    <input
                      type="text"
                      placeholder="অ্যাট্রিবিউট গ্রুপের নাম (যেমন: হিজাব, ম্যাটেরিয়াল)"
                      value={newGroupName}
                      onChange={e => setNewGroupName(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); addCustomGroup(); }
                      }}
                      autoFocus
                    />
                    <button type="button" className="attr-add-btn" onClick={addCustomGroup}>
                      যোগ করুন
                    </button>
                    <button
                      type="button"
                      className="btn-cancel"
                      style={{ padding: '7px 12px', borderRadius: '6px', cursor: 'pointer', border: '1px solid #ccc' }}
                      onClick={() => { setShowNewGroupInput(false); setNewGroupName(''); }}
                    >
                      বাতিল
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="add-custom-group-btn"
                    onClick={() => setShowNewGroupInput(true)}
                  >
                    + Add Custom Attribute Group (যেমন: হিজাব, প্যাকেজিং)
                  </button>
                )}
              </div>
            </div>
          </div>
          {/* ─────────────────────────────────────────────────────────────── */}

          {/* Active Status */}
          <div className="admin-form-row checkbox-row">
            <div className="admin-form-label">Status</div>            <div className="admin-form-input">
              <label className="admin-toggle-label">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                />
                <span>Active Product</span>
              </label>
            </div>
          </div>

          {/* Featured */}
          <div className="admin-form-row checkbox-row">
            <div className="admin-form-label">Featured</div>
            <div className="admin-form-input">
              <label className="admin-toggle-label">
                <input
                  type="checkbox"
                  name="is_featured"
                  checked={formData.is_featured}
                  onChange={handleChange}
                />
                <span>Featured Product</span>
              </label>
            </div>
          </div>

          {/* Free Delivery */}
          <div className="admin-form-row checkbox-row">
            <div className="admin-form-label">Free Delivery</div>
            <div className="admin-form-input">
              <label className="admin-toggle-label">
                <input
                  type="checkbox"
                  name="free_delivery"
                  checked={formData.free_delivery}
                  onChange={handleChange}
                />
                <span>Free Home Delivery</span>
              </label>
            </div>
          </div>
        </div>

        <div className="admin-form-actions">
          <button type="button" onClick={onClose} className="btn-cancel" disabled={loading || imageUploading}>
            Cancel
          </button>
          <button type="submit" className="btn-save" disabled={loading || imageUploading}>
            {loading ? 'Saving...' : imageUploading ? 'Uploading Images...' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ProductFormModal;
